'use client';

import { cn } from '@workspace/ui/lib';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const tableVariants = cva('relative w-full overflow-x-auto', {
  variants: {
    variant: {
      default: 'md:min-h-[445px]',
      compact: 'md:min-h-0',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

function Table({
  className,
  variant,
  ...props
}: React.ComponentProps<'table'> & {} & VariantProps<typeof tableVariants>) {
  return (
    <div data-slot="table-container" className={cn(tableVariants({ variant }))}>
      <table data-slot="table" className={cn('w-full caption-top text-sm', className)} {...props} />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn('[&_tr]:border-b', className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-muted/50 data-[state=selected]:bg-muted border-b-grey-300 dark:border-b-grey-800 border-b transition-colors',
        className
      )}
      {...props}
    />
  );
}

function TableHead({ className, size, ...props }: React.ComponentProps<'th'> & { size?: number }) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-grey-500 py-1 pb-2 pl-2 text-left text-xs leading-3 font-medium whitespace-nowrap uppercase dark:text-neutral-100',
        className
      )}
      style={{ width: size ? `${size}%` : 'auto' }}
      {...props}
    />
  );
}

function TableCell({ className, size, ...props }: React.ComponentProps<'td'> & { size?: number }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'px-0 py-4 align-middle text-base leading-tight font-normal whitespace-nowrap text-neutral-200 sm:px-2 md:h-20 md:min-h-20 dark:text-neutral-100',
        className
      )}
      style={{ width: size ? `${size}%` : 'auto' }}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn(
        'border-b-grey-300 dark:border-b-grey-800 border-b pb-8 text-lg leading-normal font-medium text-neutral-200 dark:text-neutral-100',
        className
      )}
      {...props}
    />
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  tableVariants,
};
